import { apiGet, apiPost, apiPut, apiDelete } from './client';
import type { ApiResponse, PagedResponse, SearchParams } from './types';

// --- Contact Document (matches backend ContactDocument : BaseDocument) ---

export interface Contact {
  id: string;
  tenant_id: string;
  name: string;
  email: string;
  phone: string;
  title: string;
  department: string;
  customer_id: string;
  is_primary: boolean;
  notes: string;
  tags: string[];
  branch_id: string;
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
}

// --- Request DTOs (match backend CreateContactRequest / UpdateContactRequest) ---

export interface CreateContactRequest {
  name: string;
  email?: string;
  phone?: string;
  title?: string;
  department?: string;
  customer_id: string;
  is_primary?: boolean;
  notes?: string;
  tags?: string[];
}

export interface UpdateContactRequest {
  name: string;
  email?: string;
  phone?: string;
  title?: string;
  department?: string;
  customer_id: string;
  is_primary?: boolean;
  notes?: string;
  tags?: string[];
}

// --- Filter params for contact list ---

export interface ContactFilters {
  customer_id?: string;
  department?: string;
}

// --- API functions ---

const BASE = '/contacts';

export async function listContacts(
  params: SearchParams,
  filters?: ContactFilters
): Promise<PagedResponse<Contact>> {
  const query: Record<string, unknown> = {
    from: params.from ?? 0,
    size: params.size ?? 20,
  };
  if (params.keyword) query.keyword = params.keyword;
  if (params.sort_field) query.sort_field = params.sort_field;
  if (params.sort_descending !== undefined) query.sort_descending = params.sort_descending;
  if (filters?.customer_id) query.customer_id = filters.customer_id;
  if (filters?.department) query.department = filters.department;

  const response = await apiGet<PagedResponse<Contact>>(BASE, query);
  if (!response.data) {
    return { items: [], total: 0, page: 1, page_size: 20, total_pages: 0 };
  }
  return response.data;
}

export async function getContact(id: string): Promise<Contact> {
  const response = await apiGet<Contact>(`${BASE}/${id}`);
  if (!response.data) {
    throw new Error(response.message || 'Contact not found');
  }
  return response.data;
}

export async function createContact(data: CreateContactRequest): Promise<Contact> {
  const response = await apiPost<Contact>(BASE, data);
  if (!response.data) {
    throw new Error(response.message || 'Failed to create contact');
  }
  return response.data;
}

export async function updateContact(id: string, data: UpdateContactRequest): Promise<Contact> {
  const response = await apiPut<Contact>(`${BASE}/${id}`, data);
  if (!response.data) {
    throw new Error(response.message || 'Failed to update contact');
  }
  return response.data;
}

export async function deleteContact(id: string): Promise<ApiResponse<{ id: string }>> {
  return apiDelete<{ id: string }>(`${BASE}/${id}`);
}
